import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { todayISO } from "@/lib/dateUtils";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import ProjectPicker from "@/components/ProjectPicker";
import { getLastProjectId, setLastProjectId } from "@/lib/projects";
import {
  clearRecentActivitiesCache,
  getRecentActivities,
} from "@/lib/recentActivities";

export default function ManualEntryForm({
  onEntryLogged,
  previewMode = false,
  onPreviewSubmit,
}) {
  const [date, setDate] = useState(todayISO());
  const [hours, setHours] = useState("");
  const [activity, setActivity] = useState("");
  const [projectId, setProjectId] = useState(getLastProjectId());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [recentSuggestions, setRecentSuggestions] = useState([]);

  // Lazy-load the freelancer's recent activities so they can one-click fill
  // the textarea instead of re-typing the same description every day.
  // In preview/sandbox mode we'd hit the API anyway and the admin's empty
  // history is fine — guard saves a needless 401 when not signed in.
  useEffect(() => {
    if (previewMode) return;
    let cancelled = false;
    getRecentActivities().then((list) => {
      if (!cancelled) setRecentSuggestions(list || []);
    });
    return () => {
      cancelled = true;
    };
  }, [previewMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const h = parseFloat(hours);
    if (!date) return setError("Please select a date.");
    if (!Number.isFinite(h) || h <= 0 || h > 24)
      return setError("Hours must be between 0 and 24.");
    if (!activity.trim()) return setError("Please describe the activity.");

    setSubmitting(true);
    try {
      if (previewMode) {
        await onPreviewSubmit?.({
          date,
          hours: h,
          activity: activity.trim(),
          project_id: projectId || null,
        });
      } else {
        await api.post("/entries", {
          date,
          hours: h,
          activity: activity.trim(),
          project_id: projectId || null,
        });
        // Surface the just-saved activity in the typeahead next time.
        clearRecentActivitiesCache();
        getRecentActivities().then((list) => setRecentSuggestions(list || []));
        toast.success("Entry added");
        onEntryLogged?.();
      }
      setHours("");
      setActivity("");
      setLastProjectId(projectId);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add entry.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-6"
      data-testid="manual-entry-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
          Add entry manually
        </h3>
        <span className="text-xs text-slate-400">Past or today</span>
      </div>
      <form onSubmit={handleSubmit} className="mt-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
            <input
              type="date"
              value={date}
              max={todayISO()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="manual-date-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Hours</label>
            <input
              type="number"
              step="any"
              min="0.01"
              max="24"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="e.g. 2.5"
              data-testid="manual-hours-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Project</label>
          <ProjectPicker
            value={projectId}
            onChange={setProjectId}
            disabled={submitting}
            testId="manual-project-picker"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-slate-600">Activity</label>
            {recentSuggestions.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                Recent
              </span>
            )}
          </div>
          {recentSuggestions.length > 0 && (
            <div
              className="mb-1.5 flex flex-wrap gap-1"
              data-testid="manual-activity-suggestions"
            >
              {recentSuggestions.slice(0, 6).map((s) => {
                const active = activity.trim() === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setActivity(s)}
                    disabled={submitting}
                    data-testid={`manual-activity-chip-${s}`}
                    title="Use this activity"
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition truncate max-w-[180px] ${
                      active
                        ? "text-white border-transparent"
                        : "text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    style={
                      active
                        ? { background: BRAND.teal, borderColor: BRAND.teal }
                        : undefined
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            rows={2}
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
            placeholder="What did you work on?"
            data-testid="manual-activity-input"
            maxLength={500}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-none"
            style={{ "--tw-ring-color": BRAND.teal }}
            disabled={submitting}
          />
        </div>
        {error && (
          <div
            className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
            data-testid="manual-error"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            className="text-sm px-3 py-2 rounded-md bg-green-50 border border-green-200 text-green-700"
            data-testid="manual-success"
          >
            {success}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="manual-submit-button"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-white font-semibold px-5 py-2.5 rounded-lg transition disabled:opacity-70"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
          Add entry
        </button>
      </form>
    </div>
  );
}
